import { Link } from "@tanstack/react-router";
import {
  BookOpen,
  CalendarDays,
  Clock,
  ExternalLink,
  type LucideIcon,
  Megaphone,
  ReceiptText,
  Repeat,
  User,
  Users,
  X,
} from "lucide-react";
import { useState } from "react";
import { AnnouncementBody } from "@/components/announcements/AnnouncementBody";
import { ANNOUNCEMENT_ICONS } from "@/components/announcements/announcement-icons";
import { ExpiringCertsCard } from "@/components/dashboard/ExpiringCertsCard";
import { HomeCard, HomeCardHead } from "@/components/dashboard/home-card";
import { ProjectFeedbackPromptCard } from "@/components/dashboard/ProjectFeedbackPromptCard";
import { PersonAvatar } from "@/components/persons/PersonAvatar";
import { ErrorState } from "@/components/shared/error-state";
import { OriginLink } from "@/components/shared/origin-link";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type Announcement,
  useActiveAnnouncements,
  useDismissAnnouncement,
  usePriorAnnouncements,
} from "@/hooks/use-announcements";
import { type ReportCertSummary, useDashboardInsights } from "@/hooks/use-dashboard-insights";
import { useMyPerson, usePersonDirectReports } from "@/hooks/use-persons";
import { useSession } from "@/lib/auth-client";
import { formatMonthYear, formatRelative } from "@/lib/dates";
import { cn } from "@/lib/utils";

// Friendly intranet "home" — a warm welcome, quick links, company news, and
// who's around. Identical for every employee; managers additionally get a
// direct-reports card and their team's expiring-certifications card. Company-
// wide workforce signal (org-health tiles + org-wide certs) lives on the
// admin-only Org Health page, not here.

function greetingForHour(hour: number): string {
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

function firstName(name: string): string {
  return name.trim().split(/\s+/).filter(Boolean)[0] ?? name;
}

/** "Jul 2026" from a date-only start/rehire value — no TZ drift. */
function joinMonth(iso: string): string {
  return formatMonthYear(iso) ?? iso;
}

/** "Jun 3, 2026" — when an announcement first went live. */
function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// Connections out to NOS (time, expenses, PTO) and the employee handbook.
const NOS_LOG_TIME =
  "https://os.nymbl.app/app/My%20NOS/Enter%20Time%20Entry%20Detail?$mode=Insert&$activepanel=Time+Entry+Details";
const NOS_SUBMIT_EXPENSE =
  "https://os.nymbl.app/app/My%20NOS/Reimbursement?$mode=Insert&$activepanel=Reimbursement";
const NOS_PTO_REQUEST =
  "https://os.nymbl.app/app/My%20NOS/Enter%20PTO%20Details?$mode=Insert&$activepanel=PTO+Request";
const HANDBOOK_URL =
  "https://docs.google.com/document/d/1yYGnL4u12owaRYgMJRUmsWEBgL99aCas5iiF9YuCwrI/edit?usp=sharing";

interface QuickLink {
  icon: LucideIcon;
  label: string;
  to?: string;
  params?: Record<string, string>;
  href?: string;
}

export function MyDashboardHome() {
  const { data: session } = useSession();
  const { data: me } = useMyPerson();
  const announcementsQuery = useActiveAnnouncements();
  const announcements = announcementsQuery.data ?? [];
  const dismiss = useDismissAnnouncement();
  const insightsQuery = useDashboardInsights();
  const insights = insightsQuery.data;
  const [historyOpen, setHistoryOpen] = useState(false);

  const displayName = me?.name ?? session?.user?.name ?? "there";

  // "New to Nymbl" — active people within 30 days of their start/rehire date
  // (computed server-side). The panel hides entirely once no one qualifies.
  const newJoiners = insights?.newJoiners ?? [];
  const showNewJoiners = insightsQuery.isLoading || insightsQuery.isError || newJoiners.length > 0;

  const isManager = insights?.scope === "manager";
  // Only managers get their team's expiring-certs card on the home page; the
  // org-wide view is admin-only and now lives on the Org Health page. Hidden
  // until scope is known so it doesn't flash for the non-manager majority.
  const showTeamCerts = isManager;

  const quickLinks: QuickLink[] = [
    { icon: Clock, label: "Log Time", href: NOS_LOG_TIME },
    { icon: CalendarDays, label: "PTO Request", href: NOS_PTO_REQUEST },
    { icon: ReceiptText, label: "Submit Expense", href: NOS_SUBMIT_EXPENSE },
    { icon: BookOpen, label: "Handbook", href: HANDBOOK_URL },
    { icon: Users, label: "Team Directory", to: "/dashboard/persons" },
    // Only offer "My Profile" when the signed-in user is linked to a person
    // record — otherwise the tile has nowhere to go.
    ...(me
      ? [
          {
            icon: User,
            label: "My Profile",
            to: "/dashboard/persons/$personId",
            params: { personId: me.id },
          } satisfies QuickLink,
        ]
      : []),
  ];

  const greeting = greetingForHour(new Date().getHours());

  return (
    <div className="space-y-5">
      {/* Hero banner */}
      <div
        className="relative flex items-center gap-5 overflow-hidden rounded-[14px] px-6 py-5 text-white"
        style={{ background: "linear-gradient(120deg, #001847 0%, #1A2A66 60%, #2D9BD6 130%)" }}
      >
        <div
          className="pointer-events-none absolute -right-10 -top-10 size-60"
          style={{ background: "radial-gradient(circle, rgba(45,155,214,0.3), transparent 70%)" }}
        />
        <PersonAvatar
          name={displayName}
          seed={me?.id ?? displayName}
          size="lg"
          className="relative rounded-full bg-white/15 text-white"
          imageUrl={me?.avatarUrl}
        />
        <div className="relative">
          <h1 className="m-0 font-display text-2xl font-bold tracking-tight">
            {greeting}, {firstName(displayName)}.
          </h1>
          <p className="mt-1 max-w-[520px] text-sm text-white/70">
            Welcome back. Here&rsquo;s what&rsquo;s happening across Nymbl today.
          </p>
        </div>
      </div>

      {/* Quick links */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {quickLinks.map((q) => {
          const inner = (
            <>
              <span className="flex size-10 items-center justify-center rounded-[10px] bg-accent text-brand-blue">
                <q.icon className="size-5" />
              </span>
              <span className="text-sm font-semibold leading-tight text-foreground">{q.label}</span>
            </>
          );
          const tileClass =
            "flex flex-col items-start gap-2.5 rounded-xl border bg-card p-3.5 text-left transition-[transform,box-shadow,border-color] duration-200 ease-out hover:-translate-y-0.5 hover:border-input hover:shadow-md";
          if (q.href) {
            return (
              <a key={q.label} href={q.href} target="_blank" rel="noreferrer" className={tileClass}>
                {inner}
              </a>
            );
          }
          return q.to ? (
            <Link key={q.label} to={q.to} params={q.params} className={tileClass}>
              {inner}
            </Link>
          ) : null;
        })}
      </div>

      {/* Appears only when a project the viewer was on has just been marked complete. */}
      <ProjectFeedbackPromptCard />

      {/* Two-column body */}
      <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-[1.6fr_1fr]">
        {/* Left — news + workforce signal */}
        <div className="flex min-w-0 flex-col gap-3.5">
          <HomeCard>
            <HomeCardHead
              title="Announcements"
              action={
                <button
                  type="button"
                  onClick={() => setHistoryOpen(true)}
                  className="text-xs font-semibold text-muted-foreground hover:text-foreground"
                >
                  Prior announcements
                </button>
              }
            />
            <div className="px-5 py-1.5">
              {announcementsQuery.isLoading ? (
                <div className="flex flex-col gap-4 py-3">
                  {[0, 1].map((i) => (
                    <div key={i} className="flex gap-3.5">
                      <Skeleton className="size-10 rounded-[10px]" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-3.5 w-52" />
                        <Skeleton className="h-3 w-full" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : announcementsQuery.isError ? (
                <ErrorState
                  className="py-4"
                  message="Couldn't load announcements"
                  onRetry={() => announcementsQuery.refetch()}
                />
              ) : announcements.length === 0 ? (
                <p className="py-5 text-center text-xs text-muted-foreground">
                  You&rsquo;re all caught up. Dismissed ones live under &ldquo;Prior
                  announcements&rdquo;.
                </p>
              ) : (
                <div className="flex flex-col">
                  {announcements.map((a) => {
                    const AnnIcon = ANNOUNCEMENT_ICONS[a.icon] ?? Megaphone;
                    return (
                      <div
                        key={a.id}
                        className={cn(
                          "flex gap-3.5 py-4",
                          a.bgColor
                            ? "my-1.5 rounded-[10px] px-3.5 first:mt-1 last:mb-1"
                            : "border-b first:pt-1 last:border-0 last:pb-0.5",
                        )}
                        // Mixed with the card ground so the tint stays subtle
                        // in light mode and doesn't blind in dark mode.
                        style={
                          a.bgColor
                            ? {
                                backgroundColor: `color-mix(in srgb, ${a.bgColor} 45%, var(--card))`,
                              }
                            : undefined
                        }
                      >
                        <div className="flex size-10 shrink-0 items-center justify-center rounded-[10px] bg-navy text-white">
                          <AnnIcon className="size-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="mb-0.5 flex items-center gap-2">
                            <span className="text-2xs text-muted-foreground">
                              {formatRelative(new Date(a.occurrenceStart))}
                            </span>
                            <span className="text-2xs text-muted-foreground">·</span>
                            <span className="text-2xs text-muted-foreground">
                              First shown {shortDate(a.startsAt)}
                            </span>
                            {a.recurrence !== "none" && (
                              <Repeat
                                className="size-3 text-muted-foreground"
                                role="img"
                                aria-label="Recurring"
                              />
                            )}
                          </div>
                          <h4 className="m-0 mb-0.5 font-display text-base font-bold tracking-tight text-card-foreground">
                            {a.title}
                          </h4>
                          <AnnouncementBody
                            html={a.body}
                            className="text-sm leading-normal text-muted-foreground text-pretty"
                          />
                        </div>
                        <AnnouncementLinkButton announcement={a} className="self-center" />
                        <button
                          type="button"
                          onClick={() => dismiss.mutate(a.id)}
                          disabled={dismiss.isPending}
                          className="self-start rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                          title="Dismiss"
                          aria-label={`Dismiss "${a.title}"`}
                        >
                          <X className="size-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </HomeCard>

          {showTeamCerts && (
            <ExpiringCertsCard
              title="Your team — expiring certifications"
              insight={insights?.expiringCerts}
              isLoading={insightsQuery.isLoading}
              isError={insightsQuery.isError}
              onRetry={() => insightsQuery.refetch()}
            />
          )}
        </div>

        {/* Right — people */}
        <div className="flex min-w-0 flex-col gap-3.5">
          {me && <DirectReportsCard personId={me.id} certSummary={insights?.reportCertSummary} />}

          {showNewJoiners && (
            <HomeCard>
              <HomeCardHead title="New to Nymbl" />
              <div className="px-5 py-1.5">
                <PeopleList
                  isLoading={insightsQuery.isLoading}
                  isError={insightsQuery.isError}
                  onRetry={() => insightsQuery.refetch()}
                  people={newJoiners}
                  empty="No recent joiners."
                  renderTrailing={(p) => (
                    <span className="shrink-0 text-2xs text-muted-foreground">
                      {p.rehire ? "Rejoined" : "Joined"} {joinMonth(p.joinedAt)}
                    </span>
                  )}
                />
              </div>
            </HomeCard>
          )}
        </div>
      </div>

      {historyOpen && <PriorAnnouncementsDialog onClose={() => setHistoryOpen(false)} />}
    </div>
  );
}

/**
 * The optional admin-configured call-to-action on an announcement,
 * e.g. "Sign off time" → the NOS time-entry page. Opens in a new tab.
 */
function AnnouncementLinkButton({
  announcement: a,
  className,
}: {
  announcement: Announcement;
  className?: string;
}) {
  if (!a.linkUrl || !a.linkLabel) return null;
  return (
    <a
      href={a.linkUrl}
      target="_blank"
      rel="noreferrer"
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-accent px-3 py-1.5 text-xs font-semibold text-brand-blue transition-colors hover:bg-accent/70",
        className,
      )}
    >
      {a.linkLabel}
      <ExternalLink className="size-3" />
    </a>
  );
}

/**
 * "Prior announcements" — the announcements this user dismissed, newest
 * dismissal first. A recurring announcement dismissed across several
 * occurrences appears once, stamped with the latest dismissal.
 */
function PriorAnnouncementsDialog({ onClose }: { onClose: () => void }) {
  const priorQuery = usePriorAnnouncements(true);
  const rows = priorQuery.data ?? [];

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Prior announcements</DialogTitle>
          <DialogDescription>Announcements you have already seen.</DialogDescription>
        </DialogHeader>
        {priorQuery.isLoading ? (
          <div className="flex flex-col gap-4 py-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-3.5 w-56" />
                <Skeleton className="h-3 w-full" />
              </div>
            ))}
          </div>
        ) : priorQuery.isError ? (
          <ErrorState
            className="py-4"
            message="Couldn't load prior announcements"
            onRetry={() => priorQuery.refetch()}
          />
        ) : rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Nothing here yet — announcements you dismiss land here.
          </p>
        ) : (
          <div className="flex flex-col">
            {rows.map((a) => (
              <div key={a.id} className="border-b py-3.5 first:pt-1 last:border-0">
                <div className="mb-0.5 flex items-center gap-2">
                  <span className="text-2xs text-muted-foreground">
                    Dismissed {formatRelative(new Date(a.dismissedAt))}
                  </span>
                  <span className="text-2xs text-muted-foreground">·</span>
                  <span className="text-2xs text-muted-foreground">
                    First shown {shortDate(a.startsAt)}
                  </span>
                  {a.recurrence !== "none" && (
                    <Repeat
                      className="size-3 text-muted-foreground"
                      role="img"
                      aria-label="Recurring"
                    />
                  )}
                </div>
                <h4 className="m-0 mb-0.5 font-display text-sm font-bold tracking-tight text-card-foreground">
                  {a.title}
                </h4>
                <div className="flex items-center gap-3">
                  <AnnouncementBody
                    html={a.body}
                    className="min-w-0 flex-1 text-sm leading-normal text-muted-foreground"
                  />
                  <AnnouncementLinkButton announcement={a} />
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * "Your direct reports" — only rendered for people who manage someone.
 * Admins and individual contributors without reports see nothing here.
 * For managers, each report shows a small cert-attention badge when the
 * insights payload carries a summary for them.
 */
function DirectReportsCard({
  personId,
  certSummary,
}: {
  personId: string;
  certSummary?: Record<string, ReportCertSummary>;
}) {
  const reportsQuery = usePersonDirectReports(personId);
  const reports = reportsQuery.data ?? [];

  if (reportsQuery.isError) {
    return (
      <HomeCard>
        <HomeCardHead title="Your direct reports" />
        <ErrorState
          className="py-6"
          message="Couldn't load your direct reports"
          onRetry={() => reportsQuery.refetch()}
        />
      </HomeCard>
    );
  }

  // Nothing until we know there ARE reports — rendering a skeleton card during
  // load would flash-then-vanish for everyone who manages no one.
  if (reports.length === 0) return null;

  return (
    <HomeCard>
      <HomeCardHead
        title="Your direct reports"
        action={<span className="text-xs text-muted-foreground">{reports.length}</span>}
      />
      <div className="px-5 py-1.5">
        <PeopleList
          isLoading={false}
          people={reports}
          empty="No direct reports."
          renderTrailing={(p) => {
            const s = certSummary?.[p.id];
            if (!s || (s.expiringSoon === 0 && s.unstarted === 0)) return null;
            return (
              <div className="flex shrink-0 items-center gap-1.5">
                {s.expiringSoon > 0 && (
                  <span
                    title="Certifications expired or expiring within 60 days"
                    className="rounded-full bg-warning-bg px-2 py-0.5 text-3xs font-semibold text-warning-foreground"
                  >
                    {s.expiringSoon} exp
                  </span>
                )}
                {s.unstarted > 0 && (
                  <span
                    title="Certifications assigned but not started"
                    className="rounded-full bg-muted px-2 py-0.5 text-3xs font-semibold text-muted-foreground"
                  >
                    {s.unstarted} to start
                  </span>
                )}
              </div>
            );
          }}
        />
      </div>
    </HomeCard>
  );
}

interface PersonListItem {
  id: string;
  name: string;
  title: string | null;
  avatarUrl: string | null;
}

function PeopleList<T extends PersonListItem>({
  isLoading,
  isError,
  onRetry,
  people,
  empty,
  renderTrailing,
}: {
  isLoading: boolean;
  isError?: boolean;
  onRetry?: () => void;
  people: T[];
  empty: string;
  renderTrailing?: (person: T) => React.ReactNode;
}) {
  if (isLoading) {
    return (
      <div className="flex flex-col gap-3 py-2">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="size-8 rounded-full" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-3 w-28" />
              <Skeleton className="h-2.5 w-20" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Distinguish a failed load from a genuinely empty list — otherwise an error
  // silently reads as "nobody's new here".
  if (isError) {
    return <ErrorState className="py-4" message="Couldn't load people" onRetry={onRetry} />;
  }

  if (people.length === 0) {
    return <p className="py-4 text-center text-xs text-muted-foreground">{empty}</p>;
  }

  return (
    <div className="flex flex-col">
      {people.map((p) => (
        <OriginLink
          key={p.id}
          to="/dashboard/persons/$personId"
          params={{ personId: p.id }}
          className="group flex items-center gap-3 border-b border-dashed py-2 last:border-0"
        >
          <PersonAvatar name={p.name} seed={p.id} size="sm" imageUrl={p.avatarUrl} />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-foreground group-hover:text-brand-blue">
              {p.name}
            </div>
            <div className="truncate text-2xs text-muted-foreground">{p.title ?? "—"}</div>
          </div>
          {renderTrailing?.(p)}
        </OriginLink>
      ))}
    </div>
  );
}
